import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useAdminApplicationScope } from '../apps/AdminApplicationScope';
import { Paperclip, X } from 'lucide-react';
import { useAuth } from '@edusense/shared-auth-core';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import {
  countPendingSupportInquiries,
  normalizeSupportInquiries,
  sortSupportInquiriesByCreatedDesc,
  type SupportAttachment,
  type SupportInquiry,
} from '../types/support';

type StatusFilter = 'all' | 'pending' | 'replied' | 'closed';
const MAX_REPLY_FILES = 3;
const MAX_REPLY_FILE_BYTES = 20 * 1024 * 1024;
const MAX_REPLY_TOTAL_BYTES = 40 * 1024 * 1024;
const ALLOWED_REPLY_TYPES = new Set([
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/gif',
  'video/mp4',
  'video/webm',
  'video/quicktime',
]);

function AttachmentPreview({ attachment, headers }: { attachment: SupportAttachment; headers: Record<string, string> }) {
  const [objectUrl, setObjectUrl] = useState('');
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let active = true;
    let createdUrl = '';
    setFailed(false);
    setObjectUrl('');
    void axios.get<Blob>(attachment.url, { headers, withCredentials: true, responseType: 'blob' })
      .then((response) => {
        if (!active) return;
        createdUrl = URL.createObjectURL(response.data);
        setObjectUrl(createdUrl);
      })
      .catch(() => active && setFailed(true));
    return () => {
      active = false;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [attachment.url, headers]);

  if (failed) return <div className="rounded-md border border-red-200 p-3 text-sm text-red-600">첨부를 불러오지 못했습니다: {attachment.original_name}</div>;
  if (!objectUrl) return <div className="rounded-md border p-3 text-sm text-muted-foreground">첨부 불러오는 중…</div>;
  return (
    <figure className="overflow-hidden rounded-lg border bg-muted/20">
      {attachment.content_type.startsWith('video/')
        ? <video src={objectUrl} controls className="max-h-96 w-full object-contain" />
        : <img src={objectUrl} alt={attachment.original_name} className="max-h-96 w-full object-contain" />}
      <figcaption className="flex justify-between gap-3 px-3 py-2 text-xs text-muted-foreground">
        <span className="truncate">{attachment.original_name}</span>
        <span>{(attachment.byte_size / 1024 / 1024).toFixed(1)}MB</span>
      </figcaption>
    </figure>
  );
}

export default function SupportAdminPage() {
  const { token: accessToken } = useAuth();
  // 인박스는 앱마다 따로 본다 — videoeditor 홈페이지의 문의가 사주 문의 사이에 섞여 보이면 안 된다.
  const { application, applicationId } = useAdminApplicationScope();
  const [inquiries, setInquiries] = useState<SupportInquiry[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [selectedInquiry, setSelectedInquiry] = useState<SupportInquiry | null>(null);
  const [replyContent, setReplyContent] = useState('');
  const [replyFiles, setReplyFiles] = useState<File[]>([]);
  const [replyFileError, setReplyFileError] = useState('');
  const [isMutating, setIsMutating] = useState(false);
  const [filter, setFilter] = useState<StatusFilter>('all');

  const getAuthHeaders = useCallback(() => {
    const headers: Record<string, string> = accessToken ? { Authorization: `Bearer ${accessToken}` } : {};
    const xsrfCookie = document.cookie.split('; ').find(row => row.startsWith('XSRF-TOKEN='));
    if (xsrfCookie) headers['X-XSRF-TOKEN'] = xsrfCookie.split('=')[1];
    return headers;
  }, [accessToken]);

  const previewHeaders = useMemo(() => getAuthHeaders(), [getAuthHeaders]);

  const fetchInquiries = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const response = await axios.get<SupportInquiry[]>(`/api/support/admin/inquiries?limit=200&app=${encodeURIComponent(applicationId)}`, {
        headers: getAuthHeaders(),
        withCredentials: true,
      });
      setInquiries(sortSupportInquiriesByCreatedDesc(normalizeSupportInquiries(response.data)));
    } catch (error) {
      console.error('Failed to fetch inquiries:', error);
      setLoadError('문의 목록을 불러오지 못했습니다. 인증 상태와 서버 연결을 확인해 주세요.');
    } finally {
      setLoading(false);
    }
  }, [applicationId, getAuthHeaders]);

  useEffect(() => { void fetchInquiries(); }, [fetchInquiries]);

  const pendingCount = useMemo(() => countPendingSupportInquiries(inquiries), [inquiries]);
  const shownInquiries = useMemo(
    () => filter === 'all' ? inquiries : inquiries.filter((inquiry) => inquiry.status === filter),
    [filter, inquiries],
  );

  const handleReply = async () => {
    if (!selectedInquiry || !replyContent.trim()) return;
    setIsMutating(true);
    try {
      const form = new FormData();
      form.append('reply_content', replyContent.trim());
      replyFiles.forEach((file) => form.append('attachments', file));
      await axios.put(`/api/support/admin/inquiries/${selectedInquiry.id}/reply`, form, {
        headers: getAuthHeaders(),
        withCredentials: true,
      });
      setReplyContent('');
      setReplyFiles([]);
      setReplyFileError('');
      setSelectedInquiry(null);
      await fetchInquiries();
    } catch (error) {
      console.error('Failed to send reply:', error);
      alert('답변 등록에 실패했습니다. 답변은 전송되지 않았습니다.');
    } finally {
      setIsMutating(false);
    }
  };

  const handleReplyFiles = (event: React.ChangeEvent<HTMLInputElement>) => {
    const incoming = Array.from(event.target.files || []);
    event.target.value = '';
    setReplyFileError('');
    const nextFiles = [...replyFiles, ...incoming];
    if (nextFiles.length > MAX_REPLY_FILES) {
      setReplyFileError('답변 첨부는 최대 3개까지 가능합니다.');
      return;
    }
    if (incoming.some((file) => !ALLOWED_REPLY_TYPES.has(file.type))) {
      setReplyFileError('JPG, PNG, WEBP, GIF, MP4, WEBM, MOV 파일만 첨부할 수 있습니다.');
      return;
    }
    if (incoming.some((file) => file.size > MAX_REPLY_FILE_BYTES)) {
      setReplyFileError('파일 하나의 크기는 20MB 이하여야 합니다.');
      return;
    }
    if (nextFiles.reduce((sum, file) => sum + file.size, 0) > MAX_REPLY_TOTAL_BYTES) {
      setReplyFileError('답변 첨부의 전체 크기는 40MB 이하여야 합니다.');
      return;
    }
    setReplyFiles(nextFiles);
  };

  const handleStatus = async (status: 'pending' | 'closed') => {
    if (!selectedInquiry) return;
    setIsMutating(true);
    try {
      await axios.put(`/api/support/admin/inquiries/${selectedInquiry.id}/status`, { status }, {
        headers: getAuthHeaders(), withCredentials: true,
      });
      setSelectedInquiry(null);
      await fetchInquiries();
    } catch (error) {
      console.error('Failed to change inquiry status:', error);
      alert('상태 변경에 실패했습니다.');
    } finally {
      setIsMutating(false);
    }
  };

  const getStatusBadge = (status: string) => {
    if (status === 'pending') return <span className="rounded-full bg-yellow-100 px-2 py-1 text-xs font-bold text-yellow-800">대기중</span>;
    if (status === 'replied') return <span className="rounded-full bg-green-100 px-2 py-1 text-xs font-bold text-green-800">답변완료</span>;
    if (status === 'closed') return <span className="rounded-full bg-gray-200 px-2 py-1 text-xs font-bold text-gray-700">처리완료</span>;
    return <span className="rounded-full bg-gray-100 px-2 py-1 text-xs font-bold text-gray-800">{status}</span>;
  };

  const getTypeLabel = (type: string) => ({ bug: '버그/오류', payment: '결제/환불', suggestion: '건의사항', other: '기타' }[type] || type);

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-3xl font-bold tracking-tight">고객 문의 (Support)</h1>
            <Badge variant="outline">{application.label}</Badge>
            {pendingCount > 0 ? <Badge variant="warning">+{pendingCount} 대기</Badge> : null}
          </div>
          <p className="mt-1 text-sm text-muted-foreground">대기 문의만 검토 후 답변하거나 처리완료로 바꾸세요. 일괄 완료하지 않습니다.</p>
        </div>
        <Button onClick={() => void fetchInquiries()} variant="outline" disabled={loading}>새로고침</Button>
      </div>

      <div className="flex flex-wrap gap-2" aria-label="문의 상태 필터">
        {(['all', 'pending', 'replied', 'closed'] as StatusFilter[]).map((status) => (
          <Button key={status} size="sm" variant={filter === status ? 'default' : 'outline'} onClick={() => setFilter(status)}>
            {{ all: '전체', pending: '대기중', replied: '답변완료', closed: '처리완료' }[status]}
          </Button>
        ))}
      </div>

      {loadError && <div className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-700">{loadError}</div>}

      <div className="rounded-md border bg-card">
        <Table>
          <TableHeader><TableRow><TableHead>상태</TableHead><TableHead>유형</TableHead><TableHead>이메일</TableHead><TableHead>첨부</TableHead><TableHead>작성일</TableHead><TableHead className="text-right">관리</TableHead></TableRow></TableHeader>
          <TableBody>
            {loading ? <TableRow><TableCell colSpan={6} className="py-8 text-center">로딩 중...</TableCell></TableRow>
              : shownInquiries.length === 0 ? <TableRow><TableCell colSpan={6} className="py-8 text-center">해당 상태의 문의가 없습니다.</TableCell></TableRow>
                : shownInquiries.map((inquiry) => (
                  <TableRow key={inquiry.id}>
                    <TableCell>{getStatusBadge(inquiry.status)}</TableCell>
                    <TableCell>{getTypeLabel(inquiry.inquiry_type)}</TableCell>
                    <TableCell>{inquiry.email}</TableCell>
                    <TableCell>{inquiry.attachments.length || inquiry.reply_attachments.length ? `문의 ${inquiry.attachments.length} · 답변 ${inquiry.reply_attachments.length}` : '—'}</TableCell>
                    <TableCell>{new Date(inquiry.created_at).toLocaleString()}</TableCell>
                    <TableCell className="text-right"><Button variant="secondary" size="sm" onClick={() => { setSelectedInquiry(inquiry); setReplyContent(''); setReplyFiles([]); setReplyFileError(''); }}>상세 / 답변</Button></TableCell>
                  </TableRow>
                ))}
          </TableBody>
        </Table>
      </div>

      {selectedInquiry && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true" aria-label="문의 상세">
          <div className="flex max-h-[90vh] w-full max-w-2xl flex-col rounded-xl bg-white shadow-lg">
            <div className="flex items-center justify-between rounded-t-xl border-b bg-white p-6"><div className="flex items-center gap-2"><h2 className="text-xl font-bold">문의 상세</h2>{getStatusBadge(selectedInquiry.status)}</div><Button variant="ghost" size="sm" onClick={() => setSelectedInquiry(null)}>✕</Button></div>
            <div className="flex-1 space-y-6 overflow-y-auto p-6">
              <div><h3 className="mb-1 text-sm font-semibold text-muted-foreground">작성자 이메일</h3><p>{selectedInquiry.email}</p></div>
              <div><h3 className="mb-1 text-sm font-semibold text-muted-foreground">문의 유형</h3><p>{getTypeLabel(selectedInquiry.inquiry_type)}</p></div>
              <div><h3 className="mb-1 text-sm font-semibold text-muted-foreground">문의 내용</h3><div className="whitespace-pre-wrap rounded-md bg-muted p-4">{selectedInquiry.content}</div></div>
              {selectedInquiry.attachments.length > 0 && <div className="space-y-3"><h3 className="text-sm font-semibold text-muted-foreground">첨부 미디어 ({selectedInquiry.attachments.length})</h3>{selectedInquiry.attachments.map((attachment) => <AttachmentPreview key={attachment.id} attachment={attachment} headers={previewHeaders} />)}</div>}
              {selectedInquiry.reply_content && <div className="space-y-3"><div><h3 className="mb-1 text-sm font-semibold text-muted-foreground">답변 내용{selectedInquiry.replied_at ? ` (${new Date(selectedInquiry.replied_at).toLocaleString()})` : ''}</h3><div className="whitespace-pre-wrap rounded-md bg-primary/10 p-4">{selectedInquiry.reply_content}</div></div>{selectedInquiry.reply_attachments.length > 0 && <div className="space-y-3"><h3 className="text-sm font-semibold text-muted-foreground">답변 첨부 ({selectedInquiry.reply_attachments.length})</h3>{selectedInquiry.reply_attachments.map((attachment) => <AttachmentPreview key={attachment.id} attachment={attachment} headers={previewHeaders} />)}</div>}</div>}
              {selectedInquiry.status === 'pending' && <div className="space-y-3"><h3 className="text-sm font-semibold text-muted-foreground">답변 작성</h3><textarea className="min-h-[150px] w-full resize-y rounded-md border p-3" maxLength={10000} placeholder="등록 즉시 앱 보관함·FCM 푸시·이메일로 안내됩니다." value={replyContent} onChange={(event) => setReplyContent(event.target.value)} /><label className="flex min-h-11 cursor-pointer items-center justify-center gap-2 rounded-md border border-dashed px-3 text-sm font-semibold hover:bg-muted"><Paperclip size={17} /> 사진·동영상 첨부<input type="file" accept="image/jpeg,image/png,image/webp,image/gif,video/mp4,video/webm,video/quicktime" multiple className="sr-only" onChange={handleReplyFiles} /></label><p className="text-xs text-muted-foreground">최대 3개 · 파일당 20MB · 전체 40MB</p>{replyFileError && <p role="alert" className="text-xs text-red-600">{replyFileError}</p>}{replyFiles.map((file, index) => <div key={`${file.name}-${file.lastModified}`} className="flex min-h-11 items-center gap-2 rounded-md bg-muted px-3 text-xs"><span className="min-w-0 flex-1 truncate">{file.name}</span><span className="text-muted-foreground">{(file.size / 1024 / 1024).toFixed(1)}MB</span><button type="button" aria-label="답변 첨부 삭제" className="grid h-9 w-9 place-items-center rounded-full hover:bg-background" onClick={() => setReplyFiles((files) => files.filter((_, fileIndex) => fileIndex !== index))}><X size={16} /></button></div>)}</div>}
            </div>
            <div className="flex flex-wrap justify-end gap-3 rounded-b-xl border-t bg-gray-50 p-6">
              <Button variant="outline" onClick={() => setSelectedInquiry(null)} disabled={isMutating}>닫기</Button>
              {selectedInquiry.status === 'closed'
                ? <Button variant="outline" onClick={() => void handleStatus('pending')} disabled={isMutating}>대기중으로 재오픈</Button>
                : <Button variant="outline" onClick={() => void handleStatus('closed')} disabled={isMutating}>처리완료</Button>}
              {selectedInquiry.status === 'pending' && <Button onClick={() => void handleReply()} disabled={!replyContent.trim() || isMutating}>{isMutating ? '처리 중...' : '답변 등록·알림 전송'}</Button>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
